import React, {Fragment, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import makeStyles from '@mui/styles/makeStyles';
import {
    CircularProgress,
    FormLabel,
    Grid,
    IconButton,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
} from '@mui/material';
import Paper from '@mui/material/Paper';
import moment from 'moment';
import config from '../../../../app/config';
import IceDetails from './iceDetails';
import _ from 'lodash';
import {useDispatch, useSelector} from 'react-redux';
import {additionalData} from '../../../../store/reducers/additionalData';
import LinkIcon from '@mui/icons-material/Link';
import {hasOneOfPermission, hasPermission, Permissions, sendUserAudit, user} from '../../../../store/reducers/user';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MessageBox from '../../../../util/messageBox';
import {addNoteForPatient} from '../../../../store/reducers/notes';
import API from '../../../../app/api';
import BiotechIcon from '@mui/icons-material/Biotech';
import Notes from '../../../../util/notes';
import Formatter from '../../../../util/formatter';
import SummarizeIcon from '@mui/icons-material/Summarize';
import {currentMap} from '../../../../store/reducers/ward';
import {setSnackbar} from '../../../../store/reducers/globals';
import AgyleSelectField from '../../../../components/ui/selector/agyleSelectField';
import AgyleTextField from '../../../../components/ui/fields/agyleTextField';
import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';
import AgyleSwitch from '../../../../components/ui/switch/agyleSwitch';
import AgyleButton from '../../../../components/ui/buttons/agyleButton';
import useOpenState from '../../../../hooks/useOpenState';
import clsx from 'clsx';
import './ice.css';
import {getTableScrollbar} from '../../../../util/scrollbar';
import Tooltip from '@mui/material/Tooltip';
import {calculateArrivalDate} from '../../../../util/patientUtil';
import {PATIENT_CONTEXT_TYPE} from '../../../../util/patientContextUtil';
import {ctxSplitScreen, openPatientForm} from '../../../../store/reducers/patientContext';
import { AUDIT_TYPES } from '../../../../util/auditUtil';

export const useIceStyles = makeStyles((theme) => ({
    root: {
        width: '100%',
        height: '99%',
        marginTop: 6,
    },
    modalBackground: {
        backgroundColor: theme.palette.mode === 'light' ? '#ffffff' : '#313233',
    },
    search: {
        overflow: 'hidden',
        backgroundColor: theme.palette.mode === 'light' ? '#FFFFFF' : '#222525',
        backgroundImage: 'none',
        boxShadow: 'none',
        //   padding: 10,
    },
    table: {
        marginTop: 5,
        borderCollapse: 'separate',
        borderSpacing: '0px 4px',
        paddingRight: 5,
    },
    headCell: {
        fontSize: 14,
        background: theme.palette.background.paper,
        '&.left': {
            borderTopLeftRadius: 4,
            borderBottomLeftRadius: 4,
        },
        '&.right': {
            borderTopRightRadius: 4,
            borderBottomRightRadius: 4,
        },
    },
    rowCell: {
        fontSize: 'small',
        backgroundColor: theme.palette.mode === 'light' ? '#FFFFFF' : '#313233',
        '&.left': {
            borderTopLeftRadius: 4,
            borderBottomLeftRadius: 4,
            padding: 0,
            paddingLeft: 5,
        },
        '&.right': {
            borderTopRightRadius: 4,
            borderBottomRightRadius: 4,
        },
    },
    tableRow: {
        cursor: 'pointer',
        '&:hover': {
            background: theme.palette.mode === 'light' ? '#414141' : '#b1abab',
        },
    },
    iframe: {
        width: '100%',
        height: config?.actionButtonsOnBottomBar ? '99%' : '95%',
    },
    toolbar: {
        fontSize: 'small',
    },
    loading: {
        textAlign: 'center',
        fontWeight: 'bold',
        fontSize: 'x-large',
    },
    label: {
        color: theme.palette.mode === 'light' ? '#626466' : '#ACAFB3',
        fontWeight: 'bold',
        fontSize: 14,
        marginLeft: 2,
    },
    selectField: {
        borderRadius: 4,
        border: `1px solid ${theme.palette.mode === 'light' ? '#BFC2C7' : '#626466'}`,
        backgroundColor: theme.palette.mode === 'light' ? '#FFFFFF' : '#313233',
        height: 35,
        '&::placeholder': {
            fontStyle: 'italic',
        },
    },
    actionButtons: {
        marginTop: 15,
        marginBottom: 5,
        marginLeft: 'auto',
        float: 'right',
        display: 'flex',
        flexWrap: 'nowrap',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
    },
    actionBtn: {
        backgroundColor: theme.palette.mode === 'light' ? '#FFFFFF' : '#494B4C',
        borderRadius: 6,
    },
    divider: {
        margin: '10px 0',
    },
    actions: {
        display: 'flex',
        flexDirection: 'column',
    },
    header: {
        '& .MuiTableCell-head': {
            backgroundColor: theme.palette.mode === 'light' ? '#eff1f3' : '#494B4C',
        },
    },
    colorStudies: {
        height: 35,
        width: 10,
        marginLeft: 5,
    },
    abnormal: {
        backgroundColor: '#85200c',
        color: '#fff',
        borderRadius: 8,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 25,
        height: 25,
        marginLeft: 5,
    },
    range: {
        float: 'right',
        paddingRight: 20,
        fontStyle: 'italic',
    },
    invInfo: {
        backgroundColor: theme.palette.background.field,
        borderRadius: 6,
        display: 'flex',
        flexWrap: 'nowrap',
        flexDirection: 'row',
        alignItems: 'center',
        padding: '4px 8px',
        marginBottom: 8,
    },
    invInfoText: {
        fontWeight: 'bold',
        display: 'flex',
    },
    groupBar: {
        borderRadius: 8,
        width: '100%',
        height: 5,
        margin: '2px 0',
    },
}));

const ICE = (props) => {
    const classes = useIceStyles();
    const {t} = useTranslation();
    const patientAddData = useSelector(additionalData);
    const dispatch = useDispatch();
    const userObject = useSelector(user);
    const tester = Formatter.formatUserFullName(userObject);
    const controller = new AbortController();
    const patientType = props.patientType;

    const splitScreen = useSelector(ctxSplitScreen);
    const isSSEnabled = splitScreen?.enabled;

    // Map
    const newCurrentMap = useSelector(currentMap);
    const [actionButtons, setActionButtons] = useState([]);

    // Reports state
    const [reports, setReports] = useState([]);
    const [original, setOriginal] = useState([]);
    const [selectedReport, setSelectedReport] = useState(null);
    const [isDetailsOpen, openDetails, closeDetails] = useOpenState(false, false);
    const [loading, setLoading] = useState(true);

    // Pagination
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    // Sorting
    const [orderBy, setOrderBy] = useState('dateTimeReported');
    const [orderDirection, setOrderDirection] = useState('desc');

    // iFrame
    const [viewMode, setViewMode] = useState('list');
    const [url, setUrl] = useState(null);

    // Filters
    const [filterValue, setFilterValue] = useState({
        discipline: '',
        status: '',
        investigation: '',
        clinician: '',
        location: '',
        sample: '',
        admission: true,
    });
    const [filters, setFilters] = useState({
        discipline: [],
        status: [],
        clinician: [],
        location: [],
    });
    const firstUpdate = useRef(true);

    // Message Box Opener
    const [isResetIceOpen, openResetIce, closeResetIce] = useOpenState(false, true);

    // Init array reports
    useEffect(() => {
        if (!_.isEmpty(patientAddData?.other?.iceReports)) {
            getReports(patientAddData.other.iceReports);
            setLoading(false);
        } else {
            API.getIceReports(props.patient?.patient?.__id, controller.signal)
                .then(arr => {
                    if (!_.isEmpty(arr?.data)) {
                        getReports(arr.data);
                    }
                })
                .finally(() => {
                    setLoading(false);
                });
        }

        return () => {
            controller.abort();
        };
    }, []);

    useEffect(() => {
        if (!firstUpdate.current) {
            const arrivalTime = moment(calculateArrivalDate(props.patient)).format(config.ecdsDateTimeFormat);
            setReports(_.filter(original, report => {
                const admission = filterValue.admission ? moment(report.dateTimeReported).isAfter(arrivalTime) : true;
                const discipline = filterValue.discipline ? report.disciplineExpansion === filterValue.discipline : true;
                const status = filterValue.status ? report.status === filterValue.status : true;
                const investigation = filterValue.investigation ? _.toLower(report.investigationsDescription).includes(_.toLower(filterValue.investigation)) : true;
                const clinician = filterValue.clinician ? report.clinician === filterValue.clinician : true;
                const location = filterValue.location ? report.location === filterValue.location : true;
                const sample = filterValue.sample ? _.toLower(report.sampleCode).includes(_.toLower(filterValue.sample)) : true;

                return admission && discipline && status && investigation && clinician && location && sample;
            }));
        }
    }, [filterValue]);

    useEffect(() => {
        const buttons = [];

        if (viewMode === 'iframe' && dispatch(hasPermission(Permissions.AGYLE_CAN_VIEW_ICE_REPORTS_INFO))) {
            buttons.push({
                name: 'backToTable',
                display: t('Back to table'),
                icon: <ArrowBackIcon/>,
                event: backToTable,
            });
        }

        if (!isSSEnabled && viewMode !== 'iframe' && !props.disableSave && dispatch(hasOneOfPermission([Permissions.AGYLE_CAN_VIEW_URINALYSIS, Permissions.AGYLE_CAN_ADD_URINALYSIS, Permissions.AGYLE_CAN_EDIT_URINALYSIS]))) {
            buttons.push({
                name: 'urinalysis',
                display: t('Urinalysis Assessment'),
                icon: <BiotechIcon/>,
                event: () => dispatch(openPatientForm('urinalysis')),
            });
        }

        if (config.enableIceDesktop && patientType !== PATIENT_CONTEXT_TYPE.TEMPORARY) {
            buttons.push({
                name: 'ice',
                display: t('Ice Desktop'),
                disabled: props.disableSave,
                icon: <SummarizeIcon/>,
                event: handleOpenUIceDesktop,
            });
        }

        setActionButtons(buttons);
    }, [viewMode, splitScreen?.enabled]);

    const getReports = (reportsArray) => {
        let mapped = [];

        const discipline = new Set();
        const status = new Set();
        const clinician = new Set();
        const location = new Set();

        _.forEach(reportsArray, report => {
            if (!report?.hidden) {
                const obj = {
                    ...report,
                    disciplineExpansion: report?.specialtyCode === '810' ? 'Radiology' : report?.discipline?.expansion,
                    investigationsDescription: report?.investigations?.[0]?.description,
                    clinician: report?.request?.clinician?.givenName + ' ' + report?.request?.clinician?.familyName,
                    samplesReceivedDateTime: report?.request?.samples?.[0]?.receivedDateTime,
                    samplesCollectedDateTime: report?.request?.samples?.[0]?.collectedDateTime,
                    sampleCode: report?.request?.samples?.[0]?.code,
                    location: report?.request?.location?.name,
                };

                // mapping fields for easier sorting
                mapped.push(obj);

                // set value for filters
                discipline.add(obj.disciplineExpansion);
                status.add(obj.status);
                clinician.add(obj.clinician);
                location.add(obj.location);
            }
        });
        setOriginal(mapped);

        const arrivalTime = moment(calculateArrivalDate(props.patient)).format(config.ecdsDateTimeFormat);

        setReports(_.filter(mapped, report => {
            return moment(report.dateTimeReported).isAfter(arrivalTime);
        }));

        setFilters({
            discipline: Array.from(discipline),
            status: Array.from(status),
            clinician: Array.from(clinician),
            location: Array.from(location),
        });
    };

    const backToTable = () => {
        setViewMode('list');
    };

    const handleOpenUIceDesktop = () => {
        const org = newCurrentMap?.mapPath === 'poole' ? 'PCAS' : 'BED';
        dispatch(sendUserAudit(props.patient, AUDIT_TYPES.OPEN_ICE, {organization: org}));
        
        API.getICEServiceResponse(props.patient?.patient?.__id, 'CUSTOM_AGL_OrderTest', org)
            .then(response => {
                if (response?.url) {
                    window.open(response?.url, '_blank').focus();
                } else {
                    dispatch(setSnackbar({type: 'error', message: 'Failed to open ICE Desktop.'}));
                }
            })
            .catch(e => {
                console.warn(e);
                dispatch(setSnackbar({type: 'error', message: 'Failed to open ICE Desktop.'}));
            });
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const sortHeader = (header) => {
        const isAsc = orderBy === header && orderDirection === 'asc';
        setOrderBy(header);
        setOrderDirection(isAsc ? 'desc' : 'asc');
    };

    const getComparator = (orderDirection, orderBy) => {
        function descComparator(a, b, orderBy) {
            if (moment(a[orderBy]).isValid() && moment(b[orderBy]).isValid()) {
                if (moment(b[orderBy]).isBefore(moment(a[orderBy]))) {
                    return -1;
                }
                if (moment(b[orderBy]).isAfter(moment(a[orderBy]))) {
                    return 1;
                }
                return 0;
            } else {
                if (b[orderBy] < a[orderBy]) {
                    return -1;
                }
                if (b[orderBy] > a[orderBy]) {
                    return 1;
                }
                return 0;
            }
        }

        return orderDirection === 'desc'
            ? (a, b) => descComparator(a, b, orderBy)
            : (a, b) => -descComparator(a, b, orderBy);
    };

    // Create sorted array
    const sortedRow = (array, comparator) => {
        const stabilizedRowArray = array.map((el, index) => [el, index]);
        stabilizedRowArray.sort((a, b) => {
            const order = comparator(a[0], b[0]);
            if (order !== 0) return order;
            return a[1] - b[1];
        });
        return stabilizedRowArray.map((el) => el[0]);
    };

    const selectReport = (row) => {
        setSelectedReport(row);
        openDetails();
    };

    const handleFilter = (value, name) => {
        firstUpdate.current = false;

        setFilterValue(prevState => ({...prevState, [name]: value}));
    };

    const clearFilters = () => {
        setFilterValue({
            admission: true,
            discipline: '',
            status: '',
            investigation: '',
            clinician: '',
            location: '',
            sample: '',
        });
    };

    const getIceReportUrl = (event, report) => {
        event.stopPropagation();
        if (dispatch(hasPermission(Permissions.AGYLE_CAN_VIEW_ICE_REPORTS_INFO))) {
            let url = window.envConfig?.iceReports?.url?.toString();
            const id = report?.id;
            const patientId = report?.request?.patient?.id;
            url = url.replace('^^ice.id^^', id);
            url = url.replace('^^ice.patientId^^', patientId);

            setUrl(url);
            setViewMode('iframe');
        }
    };

    const acknowledgeReports = () => {
        closeResetIce();
        const date = moment().toISOString();
        const text = Notes.getInvestigationsNote(props.patient?.metadata?.iceReportsCount);

        API.updatePatientInWard(props.patient.__id, [{
            'metadata.iceReset': {
                counter: props.patient?.metadata?.iceReportsCount,
                by: tester,
                login: userObject?.userName,
                when: date,
            },
        }]).then((response) => {
            if (response.data) {
                const newObj = [{
                    name: 'metadata.iceReset',
                    value: {
                        counter: props.patient?.metadata?.iceReportsCount,
                        by: tester,
                        login: userObject?.userName,
                        when: date,
                    },
                }];
                props.modifyPatient(newObj);
                dispatch(addNoteForPatient(props.patient?.patient?.__id, {
                    note: text,
                    noteType: 'iceReports',
                    patientsInWardsRef: [props.patient.__id],
                    episodeId: props?.patient?.admission?.consultantEpisodeId || null,
                }, props.patient.__id));
            }
        });
    };

    const checkCount = () => {
        const count = props.patient?.metadata?.iceReportsCount;
        if (count) {
            const iseReset = props.patient?.metadata?.iceReset?.counter;
            return iseReset ? count - iseReset : count;
        }
        return 0;
    };

    const renderCustomOption = (opt) => {
        return (
            <MenuItem
                key={opt}
                value={opt}
            >
                {opt}
            </MenuItem>
        );
    };

    const getTableHeight = () => {
        const searchHeight = document.getElementById('search')?.offsetHeight + 25;
        const actionHeight = document.getElementById('action')?.offsetHeight + 25;
        return `calc(100% - ${searchHeight + actionHeight}px)`;
    };

    const renderTable = () => {
        return (
            <>
                <Paper id="search" className={classes.search}>
                    <Grid container direction="row" spacing={1.5} alignItems="flex-end">
                        <Grid item xs={12} sm={6} md={3} style={{paddingTop: 5}}>
                            <FormLabel className={classes.label}>
                                {t('Discipline')}
                            </FormLabel>

                            <AgyleSelectField
                                onChange={(value) => handleFilter(value, 'discipline')}
                                options={filters.discipline}
                                placeholder={t('Choose from list')}
                                name={'discipline'}
                                value={filterValue.discipline}
                                clearButtonVisible
                                customObject={renderCustomOption}
                                className={classes.selectField}
                                size="small"
                                style={{marginTop: 2, minHeight: 0}}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={3} style={{paddingTop: 5}}>
                            <FormLabel className={classes.label}>
                                {t('Status')}
                            </FormLabel>

                            <AgyleSelectField
                                onChange={(value) => handleFilter(value, 'status')}
                                options={filters.status}
                                placeholder={t('Choose from list')}
                                name={'status'}
                                value={filterValue.status}
                                clearButtonVisible
                                customObject={renderCustomOption}
                                className={classes.selectField}
                                size="small"
                                style={{marginTop: 2, minHeight: 0}}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={3} style={{paddingTop: 5}}>
                            <FormLabel className={classes.label}>
                                {t('Clinician')}
                            </FormLabel>

                            <AgyleSelectField
                                onChange={(value) => handleFilter(value, 'clinician')}
                                options={filters.clinician}
                                placeholder={t('Choose from list')}
                                name={'clinician'}
                                value={filterValue.clinician}
                                clearButtonVisible
                                customObject={renderCustomOption}
                                className={classes.selectField}
                                size="small"
                                style={{marginTop: 2, minHeight: 0}}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={3} style={{paddingTop: 5}}>
                            <FormLabel className={classes.label}>
                                {t('Location')}
                            </FormLabel>

                            <AgyleSelectField
                                onChange={(value) => handleFilter(value, 'location')}
                                options={filters.location}
                                placeholder={t('Choose from list')}
                                name={'Location'}
                                value={filterValue.location}
                                clearButtonVisible
                                customObject={renderCustomOption}
                                className={classes.selectField}
                                size="small"
                                style={{marginTop: 2, minHeight: 0}}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={3} style={{paddingTop: 5}}>
                            <FormLabel className={classes.label}>
                                {t('Search Sample')}
                            </FormLabel>

                            <AgyleTextField
                                name={'sample'}
                                placeholder={t('Search all Samples')}
                                onChange={(value) => handleFilter(value?.target?.value, 'sample')}
                                style={{width: '100%', marginTop: 2}}
                                value={filterValue.sample}
                                startAdornment={<InputAdornment position="start"><SearchIcon/></InputAdornment>}
                                textFieldClass={classes.selectField}
                                debounce
                                size="small"
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={3} style={{paddingTop: 5}}>
                            <FormLabel className={classes.label}>
                                {t('Search Investigations')}
                            </FormLabel>

                            <AgyleTextField
                                name={'investigation'}
                                placeholder={t('Search all Investigations')}
                                onChange={(value) => handleFilter(value?.target?.value, 'investigation')}
                                style={{width: '100%', marginTop: 2}}
                                value={filterValue.investigation}
                                startAdornment={<InputAdornment position="start"><SearchIcon/></InputAdornment>}
                                textFieldClass={classes.selectField}
                                debounce
                                size="small"
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={3} style={{paddingTop: 5}}>
                            <div style={{marginBottom: -4}}>
                                <AgyleSwitch
                                    label={t('Only since Arrival Time') + ` ( ${moment(calculateArrivalDate(props.patient)).format(config.defaultDateTimeFormat)} )`}
                                    onChange={(value) => handleFilter(value.target.checked, 'admission')}
                                    value={filterValue.admission}
                                />
                            </div>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3} style={{paddingTop: 5}}>
                            <AgyleButton
                                onClick={clearFilters}
                                variant="contained"
                                style={{marginLeft: 'auto', minWidth: isSSEnabled ? 0 : 205}}
                            >
                                {t('Clear search and filters')}
                            </AgyleButton>
                        </Grid>
                    </Grid>
                </Paper>

                <div id="action" className={classes.actions}>
                    <div className={classes.actionButtons}>
                        {actionButtons.map(btn => (
                            <Tooltip
                                title={btn.display}
                                key={btn.name}
                            >
                                <span>
                                    <IconButton
                                        onClick={btn.event}
                                        disabled={btn.disabled}
                                        disableRipple
                                        className={classes.actionBtn}
                                        size="small"
                                    >
                                        {btn.icon}
                                    </IconButton>
                                </span>
                            </Tooltip>
                        ))}

                        <AgyleButton
                            variant="contained"
                            onClick={openResetIce}
                            disabled={!dispatch(hasPermission(Permissions.AGYLE_CAN_RESET_ICE_COUNTER))
                                || !props.patient?.metadata?.iceReportsCount
                                || checkCount() < 1
                                || props.disableSave}
                            className={classes.button}
                            style={{minWidth: 205}}
                        >
                            {t('Reset investigations counter')}
                        </AgyleButton>
                    </div>
                </div>

                <TableContainer className={clsx(getTableScrollbar(), classes.table)} style={{height: getTableHeight()}}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow className={classes.header}>
                                <TableCell size="small" className={clsx(classes.headCell, 'left')}/>

                                <TableCell size="small" className={classes.headCell}>
                                    <TableSortLabel
                                        active={orderBy === 'disciplineExpansion'}
                                        direction={orderBy === 'disciplineExpansion' ? orderDirection : 'asc'}
                                        onClick={() => sortHeader('disciplineExpansion')}
                                    >
                                        {t('Discipline')}
                                    </TableSortLabel>
                                </TableCell>

                                <TableCell size="small" className={classes.headCell}>
                                    <TableSortLabel
                                        active={orderBy === 'dateTimeReported'}
                                        direction={orderBy === 'dateTimeReported' ? orderDirection : 'asc'}
                                        onClick={() => sortHeader('dateTimeReported')}
                                    >
                                        {t('Report Date')}
                                    </TableSortLabel>
                                </TableCell>

                                <TableCell size="small" className={classes.headCell}>
                                    <TableSortLabel
                                        active={orderBy === 'sampleCode'}
                                        direction={orderBy === 'sampleCode' ? orderDirection : 'asc'}
                                        onClick={() => sortHeader('sampleCode')}
                                    >
                                        {t('Sample Code')}
                                    </TableSortLabel>
                                </TableCell>

                                <TableCell size="small" className={classes.headCell}>
                                    <TableSortLabel
                                        active={orderBy === 'status'}
                                        direction={orderBy === 'status' ? orderDirection : 'asc'}
                                        onClick={() => sortHeader('status')}
                                    >
                                        {t('Status')}
                                    </TableSortLabel>
                                </TableCell>

                                <TableCell size="small" className={classes.headCell}>
                                    <TableSortLabel
                                        active={orderBy === 'investigationsDescription'}
                                        direction={orderBy === 'investigationsDescription' ? orderDirection : 'asc'}
                                        onClick={() => sortHeader('investigationsDescription')}
                                    >
                                        {t('Investigations')}
                                    </TableSortLabel>
                                </TableCell>

                                <TableCell size="small" className={classes.headCell}>
                                    <TableSortLabel
                                        active={orderBy === 'clinician'}
                                        direction={orderBy === 'clinician' ? orderDirection : 'asc'}
                                        onClick={() => sortHeader('clinician')}
                                    >
                                        {t('Investigations Clinician')}
                                    </TableSortLabel>
                                </TableCell>

                                <TableCell size="small" className={classes.headCell}>
                                    <TableSortLabel
                                        active={orderBy === 'samplesReceivedDateTime'}
                                        direction={orderBy === 'samplesReceivedDateTime' ? orderDirection : 'asc'}
                                        onClick={() => sortHeader('samplesReceivedDateTime')}
                                    >
                                        {t('Received Sample Date')}
                                    </TableSortLabel>
                                </TableCell>

                                <TableCell size="small" className={classes.headCell}>
                                    <TableSortLabel
                                        active={orderBy === 'location'}
                                        direction={orderBy === 'location' ? orderDirection : 'asc'}
                                        onClick={() => sortHeader('location')}
                                    >
                                        {t('Location')}
                                    </TableSortLabel>
                                </TableCell>

                                <TableCell size="small"
                                           className={clsx(classes.headCell, !window?.envConfig?.iceReports?.active && 'right')}>
                                    <TableSortLabel
                                        active={orderBy === 'samplesCollectedDateTime'}
                                        direction={orderBy === 'samplesCollectedDateTime' ? orderDirection : 'asc'}
                                        onClick={() => sortHeader('samplesCollectedDateTime')}
                                    >
                                        {t('Collected Sample Date')}
                                    </TableSortLabel>
                                </TableCell>

                                {window?.envConfig?.iceReports?.active && (
                                    <TableCell size="small" className={clsx(classes.headCell, 'right')}/>
                                )}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {sortedRow(reports, getComparator(orderDirection, orderBy))
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((row) => (
                                    <TableRow
                                        key={row.id}
                                        className={classes.tableRow}
                                        onClick={() => selectReport(row)}
                                        style={{paddingBottom: 10, paddingTop: 10}}
                                    >
                                        <TableCell
                                            size="small"
                                            className={clsx(classes.rowCell, 'left')}
                                        >
                                            <div
                                                className={classes.colorStudies}
                                                style={{background: `#${row.colour}`}}
                                            />
                                        </TableCell>

                                        <TableCell
                                            size="small"
                                            className={classes.rowCell}
                                        >
                                            {row?.disciplineExpansion}
                                        </TableCell>

                                        <TableCell
                                            size="small"
                                            className={classes.rowCell}
                                        >
                                            {row.dateTimeReported ? moment(row.dateTimeReported).format(config.defaultDateTimeFormat) : ''}
                                        </TableCell>

                                        <TableCell
                                            size="small"
                                            className={classes.rowCell}
                                        >
                                            {row.sampleCode}
                                        </TableCell>

                                        <TableCell
                                            size="small"
                                            className={classes.rowCell}
                                        >
                                            {row.status}
                                        </TableCell>

                                        <TableCell
                                            size="small"
                                            className={classes.rowCell}
                                        >
                                            {row?.investigationsDescription ? _.capitalize(row.investigationsDescription) : '-'}
                                        </TableCell>

                                        <TableCell
                                            size="small"
                                            className={classes.rowCell}
                                        >
                                            {row?.clinician ? _.capitalize(row.clinician) : '-'}
                                        </TableCell>

                                        <TableCell
                                            size="small"
                                            className={classes.rowCell}
                                        >
                                            {row?.samplesReceivedDateTime ? moment(row?.samplesReceivedDateTime).format(config.defaultDateTimeFormat) : ''}
                                        </TableCell>

                                        <TableCell
                                            size="small"
                                            className={classes.rowCell}
                                        >
                                            {row?.location ? _.capitalize(row.location) : '-'}
                                        </TableCell>

                                        <TableCell
                                            size="small"
                                            className={clsx(classes.rowCell, !window?.envConfig?.iceReports?.active && 'right')}
                                        >
                                            {row?.samplesCollectedDateTime ? moment(row?.samplesCollectedDateTime).format(config.defaultDateTimeFormat) : ''}
                                        </TableCell>

                                        {window?.envConfig?.iceReports?.active && (
                                            <TableCell
                                                align="right"
                                                size="small"
                                                className={clsx(classes.rowCell, 'right')}
                                            >
                                                <AgyleButton
                                                    size="small"
                                                    colour="primary"
                                                    onClick={(event) => getIceReportUrl(event, row)}
                                                    startIcon={<LinkIcon/>}
                                                    style={{marginLeft: 'auto'}}
                                                >
                                                    {t('Link')}
                                                </AgyleButton>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[25, 50, 100]}
                    component="div"
                    count={reports.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage={t('Rows per page:')}
                    classes={{
                        toolbar: classes.toolbar,
                        spacer: classes.caption,
                    }}
                />
            </>
        );
    };

    const renderIframe = () => {

        return (
            url && (
                <>
                    {!config?.actionButtonsOnBottomBar &&
                        <div>
                            <IconButton
                                onClick={backToTable}
                            >
                                <ArrowBackIcon/>
                            </IconButton>

                            <FormLabel className={classes.label} style={{marginLeft: 5}}>
                                {t('Back to table')}
                            </FormLabel>
                        </div>
                    }
                    <iframe className={classes.iframe} src={url}/>
                </>
            )
        );
    };

    const render = () => {
        if (viewMode === 'list') {
            return renderTable();
        } else if (viewMode === 'iframe') {
            return renderIframe();
        }
    };

    const externalLinks = (url) => {
        if (dispatch(hasPermission(Permissions.AGYLE_CAN_VIEW_ICE_REPORTS_INFO))) {
            setUrl(url);
            closeDetails();
            setViewMode('iframe');
        }
    };

    return (
        <>
            {loading &&
                <div className={classes.loading}>
                    <h2>{t('Loading investigations reports. Please wait.')}</h2>
                    <CircularProgress size={100}/>
                </div>
            }

            {!loading &&
                <div className={classes.root}>
                    {render()}

                    {(isDetailsOpen && selectedReport) &&
                        <IceDetails
                            open={isDetailsOpen}
                            external={externalLinks}
                            patient={props.patient}
                            report={selectedReport}
                            allReports={original}
                            onClose={closeDetails}
                        />
                    }

                    <MessageBox
                        onClose={closeResetIce}
                        title={t('Confirmation')}
                        message={t('Are you sure you want to reset investigations counter?')}
                        haveAccept={{yes: t('Submit'), no: t('Close')}}
                        onAccept={acknowledgeReports}
                        open={isResetIceOpen}
                    />
                </div>
            }
        </>
    );
};

export default ICE;